/* =============================================================================
   CORE / AUDIT — Audit-Log-Wrapper
   =============================================================================
   Schlanker Helper, der für jede Admin-Aktion einen Audit-Log-Eintrag
   schreibt — entkoppelt via Event-Bus. Beim Customer-Pfad ist dies bewusst
   nicht aktiv (anon hat kein RLS-INSERT auf audit_log); dort übernimmt die
   SECURITY-DEFINER-RPC den Server-side Audit-Trail (Session 6).

   Backward-Compat: ans `window` gehängt, damit der noch klassische `app.js`
   die Funktion über den globalen Scope erreicht.
============================================================================ */

/**
 * Schreibt einen Audit-Log-Eintrag. Liest den aktuell eingeloggten User
 * über `App.auth.getCurrentUser()`. Bei anonymen Sessions ist der
 * Storage-Adapter ein No-Op (siehe `App.storage.audit.add()`).
 *
 * Defensive: Audit-Schreibungen dürfen NIE die aufrufende User-Aktion
 * blockieren. Wenn `getCurrentUser` (Token-Expiry mid-session), der
 * Storage-Adapter (DB-Hick) oder der Event-Emitter wirft, fangen wir
 * die Exception ab und loggen sie nur in die Console. Der Audit-Eintrag
 * geht in diesem Fall verloren — bewusste Trade-Off-Entscheidung, weil
 * Audit-Schreibungen für viele Events als "fire-and-forget"-Callbacks
 * registriert sind (siehe `src/bootstrap.js` `App.events.on(...)`-Block):
 * wenn `audit()` werfen würde, ergäbe das eine Unhandled Promise Rejection
 * im Browser, ohne dass die User-Aktion (Reservierung anlegen, Order
 * bestätigen etc.) das verhindern könnte.
 *
 * H-04 Härtung (Session 43): Defensive try/catch um die 3 awaits.
 *
 * @param {string} action - z.B. 'reservation.confirmed'
 * @param {object|null} details - JSONB-Payload für den Eintrag
 */
export async function audit(action, details) {
  if (!window.AppConfig || !window.AppConfig.features || !window.AppConfig.features.auditLog) return;
  if (!window.App || !window.App.auth || !window.App.storage) return;
  try {
    const user = await window.App.auth.getCurrentUser();
    const entry = {
      action,
      userId: user && user.id || null,
      userName: user && user.name || 'anonymous',
      details: details || null
    };
    await window.App.storage.audit.add(entry);
    if (window.App.events) {
      try {
        await window.App.events.emit('audit:log', entry);
      } catch (emitErr) {
        // Event-Emitter-Fehler isoliert behandeln — der Audit-Eintrag
        // ist bereits in der DB, nur die nachgelagerte Event-Notification
        // ist gescheitert (z.B. Subscriber-Throw). Nicht eskalieren.
        // eslint-disable-next-line no-console
        console.warn('[audit] event emit failed (entry persisted):', emitErr);
      }
    }
  } catch (err) {
    // Echter Fehler: Auth- oder Storage-Failure. Audit-Eintrag verloren.
    // Nicht werfen, sonst Unhandled Rejection in fire-and-forget-Pfaden.
    // eslint-disable-next-line no-console
    console.warn('[audit] write failed for action="' + action + '":', err && err.message ? err.message : err);
  }
}

if (typeof window !== 'undefined') {
  window.audit = audit;
}
